import { Nullable } from 'utils/types';
import { Endowment } from './Endowment';
import { Investment } from './Investment';

export enum RepaymentStrategyField {
  HaveSaleOfPropertyAndDownsizing = 'haveSaleOfPropertyAndDownsizing',
  HaveSaleOfAnotherProperty = 'haveSaleOfAnotherProperty',
  HaveInvestments = 'haveInvestments',
  HaveEndownments = 'haveEndownments',
  HaveOther = 'haveOther',

  RepaymentValueOfSaleOfPropertyAndDownsizing = 'repaymentValueOfSaleOfPropertyAndDownsizing',
  RepaymentValueOfHaveSaleOfAnotherProperty = 'repaymentValueOfHaveSaleOfAnotherProperty',
  RepaymentValueOfHaveInvestments = 'repaymentValueOfHaveInvestments',
  RepaymentValueOfHaveEndownments = 'repaymentValueOfHaveEndownments',
  RepaymentValueOfHaveOther = 'repaymentValueOfHaveOther',

  RepaymentValueTotalCalculated = 'repaymentValueTotal',
  TotalAmountToBeRepaid = 'totalAmountToBeRepaid',
  DetailsForOther = 'detailsForOther',
}

export enum PropertyField {
  Id = 'id',
  City = 'city',
  County = 'county',
  FlatNumber = 'flatNumber',
  BuildingNumber = 'buildingNumber',
  BuildingName = 'buildingName',
  Street = 'street',
  PostCode = 'postcode',
  DateOfPurchase = 'dateOfPurchase',
  CurrentMortgageBalance = 'currentMortgageBalance',
  PurchasePrice = 'purchasePrice',
}

export interface RepaymentStrategy {
  [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: Nullable<boolean>;
  [RepaymentStrategyField.HaveSaleOfAnotherProperty]: Nullable<boolean>;
  [RepaymentStrategyField.HaveInvestments]: Nullable<boolean>;
  [RepaymentStrategyField.HaveEndownments]: Nullable<boolean>;
  [RepaymentStrategyField.HaveOther]: Nullable<boolean>;

  [RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]: Nullable<number>;
  [RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]: Nullable<number>;
  [RepaymentStrategyField.RepaymentValueOfHaveInvestments]: Nullable<number>;
  [RepaymentStrategyField.RepaymentValueOfHaveEndownments]: Nullable<number>;
  [RepaymentStrategyField.RepaymentValueOfHaveOther]: Nullable<number>;
  [RepaymentStrategyField.DetailsForOther]: Nullable<string>;

  [RepaymentStrategyField.TotalAmountToBeRepaid]: Nullable<number>;
  [RepaymentStrategyField.RepaymentValueTotalCalculated]: Nullable<number>;

  properties: Property[];
  investments: Investment[];
  endowments: Endowment[];
}

export const createEmptyRepaymentStrategy = (): RepaymentStrategy => ({
  [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: null,
  [RepaymentStrategyField.HaveSaleOfAnotherProperty]: null,
  [RepaymentStrategyField.HaveInvestments]: null,
  [RepaymentStrategyField.HaveEndownments]: null,
  [RepaymentStrategyField.HaveOther]: null,

  [RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]: null,
  [RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]: null,
  [RepaymentStrategyField.RepaymentValueOfHaveInvestments]: null,
  [RepaymentStrategyField.RepaymentValueOfHaveEndownments]: null,
  [RepaymentStrategyField.RepaymentValueOfHaveOther]: null,
  [RepaymentStrategyField.RepaymentValueOfHaveOther]: null,
  [RepaymentStrategyField.DetailsForOther]: null,

  [RepaymentStrategyField.TotalAmountToBeRepaid]: null,
  [RepaymentStrategyField.RepaymentValueTotalCalculated]: null,

  properties: [],
  investments: [],
  endowments: [],
});

export type PropertyId = Nullable<number>;

export interface Property {
  [PropertyField.Id]: PropertyId;
  [PropertyField.City]: Nullable<string>;
  [PropertyField.County]: Nullable<string>;
  [PropertyField.FlatNumber]: Nullable<string>;
  [PropertyField.BuildingNumber]: Nullable<string>;
  [PropertyField.BuildingName]: Nullable<string>;
  [PropertyField.Street]: Nullable<string>;
  [PropertyField.PostCode]: Nullable<string>;
  [PropertyField.DateOfPurchase]: Nullable<string>;
  [PropertyField.CurrentMortgageBalance]: Nullable<number>;
  [PropertyField.PurchasePrice]: Nullable<number>;
}

export const createProperty = (id: PropertyId, props?: Partial<Omit<Property, 'id'>>): Property => ({
  [PropertyField.Id]: id,
  [PropertyField.City]: (props && props[PropertyField.City]) || null,
  [PropertyField.County]: (props && props[PropertyField.County]) || null,
  [PropertyField.FlatNumber]: (props && props[PropertyField.FlatNumber]) || null,
  [PropertyField.BuildingNumber]: (props && props[PropertyField.BuildingNumber]) || null,
  [PropertyField.BuildingName]: (props && props[PropertyField.BuildingName]) || null,
  [PropertyField.Street]: (props && props[PropertyField.Street]) || null,
  [PropertyField.PostCode]: (props && props[PropertyField.PostCode]) || null,
  [PropertyField.DateOfPurchase]: (props && props[PropertyField.DateOfPurchase]) || null,
  [PropertyField.CurrentMortgageBalance]: (props && props[PropertyField.CurrentMortgageBalance]) || null,
  [PropertyField.PurchasePrice]: (props && props[PropertyField.PurchasePrice]) || null,
});
